import { AfterViewInit, ViewChild } from '@angular/core';
import { Component, Input, OnInit, ChangeDetectionStrategy, SimpleChanges, OnChanges, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { ICONS } from './icon-base.component';
import { IconAnimation } from './icon.types';
import { CommonModule } from '@angular/common';

/**
 * @class
 * @description
 * IconComponent is currently under development.
 * Use with caution as it may exhibit unexpected behaviors.
 *
 * @see Examples on [README](./README.md)
 *
 */
@Component({
    selector: 'yoo-icon',
    imports: [
        CommonModule,
    ],
    templateUrl: './icon.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./icon.component.scss']
})
export class IconComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() size: number | string = 'medium';
  @Input() icon!: string;
  @Input() color: string = 'currentColor';
  @Input() background?: string[];
  @Input() animation?: IconAnimation;

  @ViewChild('backgroundElRef')
  backgroundElRef!: ElementRef<HTMLSpanElement>;

  svgIcon!: SafeHtml;
  iconSize!: string;
  bgSize!: string;
  transform!: string;
  animate!: string;

  transformDirections = ['up', 'down', 'right', 'left'];

  constructor(
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.transform = this.getTransform(this.icon);
    this.svgIcon = this.sanitizeIcon(this.icon);
    this.calculateSizes();

    if(this.animation !== undefined) {
      this.animate = this.animationsMapper(this.animation);
    }
  }

  ngAfterViewInit(): void {
    this.setBackgroundProperties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['icon'] || changes['size'] || changes['color']) {
      this.updateIcon();
    }
  }

  private updateIcon(): void {
    this.transform = this.getTransform(this.icon);
    this.svgIcon = this.sanitizeIcon(this.icon);
    this.calculateSizes();
  }

  private calculateSizes(): void {
    let baseSize: number;

    if (typeof this.size === 'number') {
      baseSize = this.size;
    } else {
      switch (this.size) {
        case 'small':
          baseSize = 16;
          break;
        case 'medium':
          baseSize = 24;
          break;
        case 'large':
          baseSize = 32;
          break;
        case 'doubleLarge':
          baseSize = 64;
          break;
        default:
          baseSize = 24; // Valor padrão caso a string não corresponda a nenhuma das opções
      }
    }

    if (this.background && baseSize > 24) {
      this.bgSize = `${baseSize}px`;
      this.iconSize = this.size === 'doubleLarge' ? `${baseSize * 0.5}px` : `${baseSize - 16}px`;
    } else {
      this.iconSize = `${baseSize}px`;
    }
  }

  animationsMapper(animation: string): string {
    let style = '';

    if(animation === 'rotation') {
      style = "rotation 1s linear infinite";
    }

    return style;
  }

  sanitizeIcon(iconName: string): SafeHtml {
    const baseIconName: string = this.getBaseIconName(iconName);
    const iconSvg = ICONS[baseIconName] || '';

    return this.sanitizer.bypassSecurityTrustHtml(iconSvg);
  }

  getBaseIconName(iconName: string): string {
    const directionPattern = /-(up|down|right|left)$/;

    if(!directionPattern) {
      return iconName
    } else {
      return iconName.replace(directionPattern, '');
    }
  }

  getTransform(iconName: string): string {
    if (iconName.includes('-up')) {
      return 'rotate(0deg)';
    } else if (iconName.includes('-down')) {
      return 'rotate(180deg)';
    } else if (iconName.includes('-right')) {
      return 'rotate(90deg)';
    } else if (iconName.includes('-left')) {
      return 'rotate(-90deg)';
    }

    return 'rotate(0)';
  }

  setBackgroundProperties(): void {
    if (this.background) {
      const bgElementRef = (this.backgroundElRef.nativeElement);
      bgElementRef.classList.add('icon-bg','border-' + this.background[0]);
      bgElementRef.style.backgroundColor = this.background[1];
    }
  }
}